import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import dompurify from "dompurify";
import { axiosPrivate } from "../../api/axios";
import styles from "./ProjectRegistration.module.css";
import { formatMoney } from "../../utils/helpers";

function DRRegistration({ Registrations: registrations = [], Event: bomEvent }) {
  const enabledRegistrations = registrations.filter((r) => r.RegistrationEnabled);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [waiverAgreed, setWaiverAgreed] = useState(false);
  const [agreementTemplate, setAgreementTemplate] = useState({});
  const [capacityAvailable, setCapacityAvailable] = useState(true);
  const [checkingCapacity, setCheckingCapacity] = useState(false);

  const handleRegistrationChange = (e) => {
    const val = parseInt(e.target.value, 10);
    const item = enabledRegistrations.find((r) => r.RegistrationID === val) ?? null;
    setSelectedRegistration(item);
    setWaiverAgreed(false);
  };

  useEffect(() => {
    const fetchAgreementTemplate = async () => {
      try {
        const response = await axiosPrivate.get("/api/agreements/2");
        setAgreementTemplate(response.data);
        //console.log("Agreement Template Properties: ", response.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchAgreementTemplate();
  }, []);

  useEffect(() => {
  if (!selectedRegistration?.RegistrationID) {
    setCapacityAvailable(true);
    return;
  }

  const checkCapacity = async () => {
    try {
      setCheckingCapacity(true);

      const response = await axiosPrivate.get(
        `/api/registrations/capacity/${selectedRegistration.RegistrationID}`
      );

       const data =
                                        response.data;


                                    /*
                                     * New API response:
                                     *
                                     * {
                                     *   available: true,
                                     *   remaining: 5,
                                     *   unlimited: false,
                                     *   capacity: 20,
                                     *   attendeeCount: 15
                                     * }
                                     */

      setCapacityAvailable(response.data.available === true);
    } catch (err) {
      console.error("Unable to check registration capacity.", err);

      // Be safe and prevent registration if we can't determine capacity
      setCapacityAvailable(false);
    } finally {
      setCheckingCapacity(false);
    }
  };

  checkCapacity();
}, [selectedRegistration]);

  const fmtMoney = (val) => {
    if (val === undefined || val === null || val === "") return "";
    const num = typeof val === "number" ? val : parseFloat(String(val).replace(/[^0-9.]/g, ""));
    if (Number.isNaN(num)) return String(val);
    return formatMoney(num);
  };

  const priceLine = (() => {
      if (!selectedRegistration) return '';
      const base = fmtMoney(selectedRegistration.RegistrationCost);
      if (selectedRegistration.RegistrationRequiredCost && selectedRegistration.RegistrationRequiredPrice != null) {
        return `${base} + ${fmtMoney(selectedRegistration.RegistrationRequiredPrice)} (Additional Cost)`;
      }
      return base;
    })();

    const depositLine = (() => {
      if(!selectedRegistration) return '';
      const hasDeposit = (selectedRegistration.RegistrationAllowDeposit && selectedRegistration.RegistrationDepositAmount != null);
      if(hasDeposit && parseInt(selectedRegistration.RegistrationDepositDueDays) >= 0){
        //deposit and due days present
        return `Deposit Amount - ${fmtMoney(selectedRegistration.RegistrationDepositAmount)}. Balance due ${selectedRegistration.RegistrationDepositDueDays} days before. You will be able to choose this option during checkout.`;
      } else if(hasDeposit){
        //just deposit present
        return `Deposit Amount - ${fmtMoney(selectedRegistration.RegistrationDepositAmount)}. You will be able to choose this option during checkout.`
      }
      else{
        return '';
      }
    })();

   const bgCheckLine = (() => {
    if (!selectedRegistration?.RequireBGCheck) return null;
  const typeMap = {
    0: 'BOM Conducted',
    1: 'Self-Conducted',
  };
  const checkType = typeMap[selectedRegistration.BGCheckType] ?? 'Standard';
  return `A background check is required for this registration (${checkType}).`;
})();

  const canRegister =
  selectedRegistration &&
  capacityAvailable &&
  (!selectedRegistration.WaiverDR || waiverAgreed);

  if (enabledRegistrations.length === 0) {
    return null;
  }

  return (
    <>
      <div className={styles.registrationPanel}>
        <div className={`${styles.regRow} ${styles.regRowMatchSelect}`}>
          <div className={styles.regSectionLabel}>Registration</div>
          <div>
            <select
              id="registrations"
              name="registrations"
              className={`DropDownField form-control ${styles.drRegSelect}`}
              onChange={handleRegistrationChange}
              value={selectedRegistration?.RegistrationID ?? ""}
            >
              <option value="">Select a registration…</option>
              {enabledRegistrations.map((opt) => (
                <option key={opt.RegistrationID} value={opt.RegistrationID}>
                  {opt.RegistrationName}
                </option>
              ))}
            </select>

            {selectedRegistration && (
              <>
                {selectedRegistration.RegistrationDescription && (
                  <div
                    className={styles.regDescription}
                    dangerouslySetInnerHTML={{
                      __html: dompurify.sanitize(selectedRegistration.RegistrationDescription),
                    }}
                  />
                )}
               {selectedRegistration.RegistrationCost > 0 && (
  <p className={styles.regPrice}>
  {priceLine}
</p>
)}

{depositLine && (
  <p className={styles.regDescription}>
    {depositLine}
  </p>
)}

{bgCheckLine && (
  <div className={styles.bgCheckWarning}>
    <span className={styles.bgCheckIcon}>⚠️</span>
    <span>{bgCheckLine}</span>
  </div>
)}


{!checkingCapacity &&
  selectedRegistration &&
  !capacityAvailable && (
    <div className={styles.capacityError}>
      This registration has reached its maximum capacity and is no longer accepting registrations.
    </div>
)}
              </>
            )}
          </div>
        </div>

        {selectedRegistration?.WaiverDR && (
          <div className={`${styles.regRow} ${styles.regRowMatchSelect}`}>
            <div className={styles.regSectionLabel}>Waiver</div>
            <div>
              <div className="form-group mb-0">
                <select
                  id="waiver"
                  name="waiver"
                  className="DropDownField form-control"
                  onChange={(e) => setWaiverAgreed(e.target.value === "1")}
                  value={waiverAgreed ? "1" : "0"}
                  aria-label={agreementTemplate?.registrationFieldLabel || "Waiver agreement"}
                >
                  <option value="0">I have not yet agreed to the waiver</option>
                  <option value="1">Yes, I agree to the waiver</option>
                </select>
                <small>
                  {agreementTemplate?.instructions}{" "}
                  <Link target="_blank" to={agreementTemplate?.pdfFileUrl}>
                    {agreementTemplate?.linkText}
                  </Link>
                </small>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={styles.actionsBelowPanel}>
        {canRegister ? (
          <Link
            className={`button-default ${styles.continueBtn}`}
            to={`/events/register/${bomEvent.EventsID}`}
            state={{
              Event: bomEvent,
              Registration: selectedRegistration,
              Quantity: 1,
            }}
          >
            Continue
          </Link>
        ) : (
          <button
  type="button"
  className={`button-default ${styles.continueBtn}`}
  disabled
>
  {selectedRegistration && !capacityAvailable
    ? "Registration Full"
    : "Continue"}
</button>
        )}
      </div>
    </>
  );
}

export default DRRegistration;
